import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  Answer,
  Conditions,
  QuestionChoice,
  AssessmentQuestion,
} from '../types/assessmentQuestion';

// A choice with a resolved submit `value` (surveyChoiceUrn, or the order as a
// string fallback — phq/gad questions carry no urn).
export type ResolvedChoice = QuestionChoice & { value: string };
export type ActiveQuestion = Omit<AssessmentQuestion, 'choices'> & { choices: ResolvedChoice[] };

// Ported from stylos's useQuestions: walks a question group one question at a
// time, applying `conditions` (stop / next-only / jump) to decide the next
// question, and calls `onFinish` once the group is answered.
export function useAssessmentQuestions(
  questions: AssessmentQuestion[],
  conditions: Conditions = {}
) {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [activeQuestion, setActiveQuestion] = useState<ActiveQuestion>();

  const parsedQuestions = useMemo<ActiveQuestion[]>(
    () =>
      (questions ?? []).map((question) => ({
        ...question,
        questionType: question.questionType || 'choice',
        choices: question.choices.map((choice) => ({
          ...choice,
          value: choice.surveyChoiceUrn ? choice.surveyChoiceUrn : `${choice.order}`,
        })),
      })),
    [questions]
  );

  const setNextActiveQuestion = useCallback(
    (answer: Answer, onFinish: () => void) => {
      if (questionIndex === parsedQuestions.length - 1) {
        return onFinish();
      }

      const condition = conditions[answer.questionKey];
      const choiceOrder = (parsedQuestions[questionIndex]?.choices ?? []).find(
        ({ value }) => value === answer.questionValue
      )?.order;

      if (condition) {
        if (condition.stopIf === choiceOrder) return onFinish();
        if (typeof condition.nextIf === 'number' && condition.nextIf !== choiceOrder) {
          return onFinish();
        }
      }

      let nextQuestionIndex = questionIndex + 1;
      if (condition?.jumpToIf && condition.jumpToIf.choice === choiceOrder) {
        const possibleNextIndex = parsedQuestions.findIndex(
          (question) => question.order === condition.jumpToIf?.nextQuestion
        );
        if (possibleNextIndex > nextQuestionIndex) nextQuestionIndex = possibleNextIndex;
      }

      return setQuestionIndex(nextQuestionIndex);
    },
    [conditions, questionIndex, parsedQuestions]
  );

  useEffect(() => {
    setActiveQuestion(parsedQuestions[questionIndex]);
  }, [questionIndex, parsedQuestions]);

  return { activeQuestion, setNextActiveQuestion };
}
